import { LoggerService } from "@/services/abstract/LoggerService";
import { LogLevel, type Logger, type LoggerConfig, type LoggerOptions } from "@/types/Logger";

/**
 * Logger Service Implementation (Adapter)
 *
 * Console-based logger with namespace filtering inspired by @libp2p/logger.
 * Uses pattern matching for enabling/disabling specific namespaces.
 */
export class LoggerServiceImpl extends LoggerService {
  private globalConfig: Required<LoggerConfig>;
  private enabledPatterns: string[] = [];
  private disabledPatterns: string[] = [];
  private readonly loggers = new Map<string, ConsoleLogger>();

  // Default colors for different log levels
  private readonly levelColors: Record<LogLevel, string> = {
    [LogLevel.ERROR]: '#ff4444',
    [LogLevel.WARN]: '#ffaa00',
    [LogLevel.INFO]: '#00aaff',
    [LogLevel.DEBUG]: '#00ff00',
    [LogLevel.TRACE]: '#888888'
  };

  // Log level hierarchy for filtering
  private readonly levelHierarchy: Record<LogLevel, number> = {
    [LogLevel.ERROR]: 0,
    [LogLevel.WARN]: 1,
    [LogLevel.INFO]: 2,
    [LogLevel.DEBUG]: 3,
    [LogLevel.TRACE]: 4
  };

  constructor() {
    super();

    // Initialize with defaults
    this.globalConfig = {
      level: LogLevel.INFO,
      enabled: '*', // Enable all by default
      includeTimestamp: true,
      formatTimestamp: () => new Date().toISOString()
    };

    // Check for environment-based configuration (similar to DEBUG env var)
    this.initializeFromEnvironment();
  }

  private initializeFromEnvironment(): void {
    // Priority: localStorage > VITE env vars > defaults
    // This allows runtime override via localStorage while supporting build-time defaults
    try {
      // Check localStorage first (runtime override takes precedence)
      let debugPattern = globalThis.localStorage?.getItem('DEBUG');
      let logLevel = globalThis.localStorage?.getItem('LOG_LEVEL');

      // Fall back to Vite environment variables if localStorage not set
      if (debugPattern == null || debugPattern.length === 0) {
        debugPattern = import.meta.env?.VITE_DEBUG_NAMESPACES ?? null;
      }

      logLevel ??= import.meta.env?.VITE_LOG_LEVEL ?? null;

      // Apply debug pattern configuration
      if (debugPattern != null && debugPattern.length > 0) {
        this.enabledPatterns = debugPattern.split(',').map(p => p.trim());
        this.globalConfig.enabled = debugPattern;
      }

      // Apply log level configuration
      if (logLevel != null) {
        const isValidLogLevel = (value: string): value is LogLevel =>
          (Object.values(LogLevel) as string[]).includes(value);

        if (isValidLogLevel(logLevel)) {
          this.globalConfig.level = logLevel;
        }
      }
    } catch {
      // localStorage or import.meta.env might not be available in some environments
      // Fall back to defaults
    }
  }

  createLogger(options: LoggerOptions): Logger {
    const namespace = options.namespace;

    // Check localStorage on every logger creation to pick up runtime changes
    this.refreshFromStorage();

    // Return existing logger if already created
    const existingLogger = this.loggers.get(namespace);
    if (existingLogger !== undefined) {
      return existingLogger;
    }

    // Create new logger
    const logger = new ConsoleLogger({
      namespace,
      level: options.level ?? this.globalConfig.level,
      enabled: options.enabled ?? this.isNamespaceEnabled(namespace),
      color: options.color,
      config: this.globalConfig,
      levelColors: this.levelColors,
      levelHierarchy: this.levelHierarchy
    });

    this.loggers.set(namespace, logger);
    return logger;
  }

  configure(config: LoggerConfig): void {
    this.globalConfig = {
      ...this.globalConfig,
      ...config
    };

    if (config.enabled !== undefined) {
      this.enabledPatterns = config.enabled.split(',').map(p => p.trim());
      this.disabledPatterns = [];
    }

    // Update all existing loggers
    this.loggers.forEach((logger, namespace) => {
      logger.updateConfig(
        config.level ?? logger.getLevel(),
        this.isNamespaceEnabled(namespace)
      );
    });
  }

  enable(pattern: string): void {
    if (!this.enabledPatterns.includes(pattern)) {
      this.enabledPatterns.push(pattern);
    }

    // Remove from disabled if present
    this.disabledPatterns = this.disabledPatterns.filter(p => p !== pattern);

    // Update matching loggers
    this.updateMatchingLoggers(pattern, true);
  }

  disable(pattern: string): void {
    if (!this.disabledPatterns.includes(pattern)) {
      this.disabledPatterns.push(pattern);
    }

    // Remove from enabled if present
    this.enabledPatterns = this.enabledPatterns.filter(p => p !== pattern);

    // Update matching loggers
    this.updateMatchingLoggers(pattern, false);
  }

  disableAll(): void {
    this.enabledPatterns = [];
    this.disabledPatterns = ['*'];

    this.loggers.forEach(logger => {
      logger.disable();
    });
  }

  refreshFromStorage(): void {
    // Re-read localStorage values and update configuration if they changed
    try {
      const debugPattern = globalThis.localStorage.getItem('DEBUG');
      if (debugPattern != null && debugPattern.length > 0) {
        const newPatterns = debugPattern.split(',').map(p => p.trim());
        // Only update if patterns changed
        if (JSON.stringify(newPatterns) !== JSON.stringify(this.enabledPatterns)) {
          this.enabledPatterns = newPatterns;
          this.globalConfig.enabled = debugPattern;
          // Update all existing loggers
          this.loggers.forEach((logger, namespace) => {
            logger.updateConfig(
              logger.getLevel(),
              this.isNamespaceEnabled(namespace)
            );
          });
        }
      }

      const logLevel = globalThis.localStorage.getItem('LOG_LEVEL');
      if (logLevel != null) {
        const isValidLogLevel = (value: string): value is LogLevel =>
          (Object.values(LogLevel) as string[]).includes(value);

        if (isValidLogLevel(logLevel) && logLevel !== this.globalConfig.level) {
          this.globalConfig.level = logLevel;
          // Update all existing loggers
          this.loggers.forEach((logger) => {
            logger.updateConfig(logLevel, logger.isEnabled());
          });
        }
      }
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Check if a namespace matches enabled patterns
   */
  private isNamespaceEnabled(namespace: string): boolean {
    // Check disabled patterns first (they take precedence)
    for (const pattern of this.disabledPatterns) {
      if (LoggerServiceImpl.matchPattern(namespace, pattern)) {
        return false;
      }
    }

    // Check enabled patterns
    for (const pattern of this.enabledPatterns) {
      if (LoggerServiceImpl.matchPattern(namespace, pattern)) {
        return true;
      }
    }

    // Default: enabled if no patterns or '*' pattern exists
    return this.enabledPatterns.length === 0 || this.enabledPatterns.includes('*');
  }

  /**
   * Match namespace against a pattern (supports wildcards)
   * @example
   * matchPattern('six3ro:p2p:helia', 'six3ro:p2p:*') // true
   * matchPattern('six3ro:auth', 'six3ro:*') // true
   * matchPattern('six3ro:auth', '*') // true
   */
  private static matchPattern(namespace: string, pattern: string): boolean {
    if (pattern === '*') return true;

    // Note: Using replace() instead of replaceAll() for ES2020 compatibility
    const regexPattern = pattern
      .replace(/[.+?^${}()|[\]\\]/g, String.raw`\$&`) // Escape special chars
      .replace(/\*/g, '.*'); // Convert * to .*

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(namespace);
  }

  /**
   * Update all loggers matching a pattern
   */
  private updateMatchingLoggers(pattern: string, enabled: boolean): void {
    this.loggers.forEach((logger, namespace) => {
      if (LoggerServiceImpl.matchPattern(namespace, pattern)) {
        if (enabled) {
          logger.enable();
        } else {
          logger.disable();
        }
      }
    });
  }
}

interface ConsoleLoggerConfig {
  namespace: string;
  level: LogLevel;
  enabled: boolean;
  color?: string;
  config?: Required<LoggerConfig>;
  levelColors?: Record<LogLevel, string>;
  levelHierarchy?: Record<LogLevel, number>;
}

/**
 * Individual logger instance for a specific namespace
 */
class ConsoleLogger implements Logger {
  private enabled: boolean;
  private level: LogLevel;
  private readonly namespace: string;
  private readonly color?: string;
  private readonly config?: Required<LoggerConfig>;
  private readonly levelColors?: Record<LogLevel, string>;
  private readonly levelHierarchy?: Record<LogLevel, number>;

  constructor(config: ConsoleLoggerConfig) {
    this.namespace = config.namespace;
    this.level = config.level;
    this.enabled = config.enabled;
    this.color = config.color;
    this.config = config.config;
    this.levelColors = config.levelColors;
    this.levelHierarchy = config.levelHierarchy;
  }

  error(...args: unknown[]): void {
    this.log(LogLevel.ERROR, args);
  }

  warn(...args: unknown[]): void {
    this.log(LogLevel.WARN, args);
  }

  info(...args: unknown[]): void {
    this.log(LogLevel.INFO, args);
  }

  debug(...args: unknown[]): void {
    this.log(LogLevel.DEBUG, args);
  }

  trace(...args: unknown[]): void {
    this.log(LogLevel.TRACE, args);
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getNamespace(): string {
    return this.namespace;
  }

  getLevel(): LogLevel {
    return this.level;
  }

  updateConfig(level: LogLevel, enabled: boolean): void {
    this.level = level;
    this.enabled = enabled;
  }

  /**
   * Internal log method that handles filtering and formatting
   */
  private log(level: LogLevel, args: unknown[]): void {
    // Skip if logger is disabled
    if (!this.enabled) return;

    // Skip if log level is below threshold
    if (this.levelHierarchy != null && this.levelHierarchy[level] > this.levelHierarchy[this.level]) {
      return;
    }

    // Format the log message
    const prefix = this.formatPrefix(level);

    // Logger service intentionally uses console for output
    /* eslint-disable no-console -- Logger needs console access */
    const consoleMethod = console[level];

    // Log with styled prefix (browser supports %c formatting)
    const color = this.levelColors?.[level] ?? '#888888';
    consoleMethod(`%c${prefix}`, `color: ${color}; font-weight: bold;`, ...args);
    /* eslint-enable no-console */
  }

  /**
   * Format the log prefix with timestamp and namespace
   */
  private formatPrefix(level: LogLevel): string {
    const parts: string[] = [];

    if (this.config?.includeTimestamp === true) {
      parts.push(this.config.formatTimestamp());
    }

    parts.push(`[${level.toUpperCase()}]`, `[${this.namespace}]`);

    return parts.join(' ');
  }
}
